import Modal from 'components/molecules/Modal/Modal';
import { ModalTypes } from 'enums/modalTypes';
import {
  CognitiveKind,
  StageCandidateFindManyDocument,
  useUpdateCoderbyteAssessmentTimeAllowanceMutation,
  useUpdateCognitiveTestTimeAllowanceMutation,
} from 'generated/graphql';
import { closeModal } from 'graphql/modals';
import { ChangeEvent, FC, useMemo, useState } from 'react';
import { useReportModalStyle } from 'views/ModalView/ReportsModal/ReportsModalStyle';
import { getTestActionName } from 'views/User/UserDetails/UserMainResets/UserReset/ActionsResultTable/ActionsTable.helpers';
import {
  IFilteredTestActionCandidate,
  TestActionType,
} from 'views/User/UserDetails/UserMainResets/UserReset/UserResets.interface';
import { Grid, Typography } from '@mui/material';
import {
  Button,
  TNotification,
  useNotification,
} from '@spotted-zebra-uk/ui-components';

interface IMitigateCircumstanceModal {
  isDefault: boolean;
  testActionCandidate: IFilteredTestActionCandidate;
  candidateId: number;
  getCurrentAssessmentId: (assesementId: number) => void;
}

const INDUCTIVE_TEST_DEFAULT_TIME = 60;
const OTHER_TESTS_DEFAULT_TIME = 180;

const MitigateCircumstanceModal: FC<IMitigateCircumstanceModal> = ({
  testActionCandidate,
  candidateId,
}) => {
  const { handleMsgType } = useNotification();
  const classes = useReportModalStyle();

  const kind = testActionCandidate.cognitiveKind;
  const isCoderbyte = testActionCandidate.type === TestActionType.CODERBYTE;
  const timeModifierPercentage =
    testActionCandidate.timeModifierPercentage || 0;

  // Cognitive testlets are timed in seconds; a Coderbyte assessment's own time
  // limit is in minutes, and may be unknown.
  const timeUnit = isCoderbyte ? 'minutes' : 'seconds';
  const defaultTestTime = useMemo(() => {
    if (isCoderbyte) {
      return testActionCandidate.timeLimitInMinutes ?? null;
    }
    if (kind === CognitiveKind.Inductive) {
      return INDUCTIVE_TEST_DEFAULT_TIME;
    }
    return OTHER_TESTS_DEFAULT_TIME;
  }, [isCoderbyte, kind, testActionCandidate.timeLimitInMinutes]);

  const calculateTimeModifierFromPercentage = (modifier: number) => {
    if (defaultTestTime === null) return null;
    // Coderbyte rounds extra minutes up, so the candidate never gets less.
    const round = isCoderbyte ? Math.ceil : Math.round;
    return round(defaultTestTime * (1 + modifier));
  };

  const getTimePercentage = () => {
    return Math.round(timeModifierPercentage * 100);
  };

  const [displayTimeValue, setDisplayTimeValue] = useState(
    calculateTimeModifierFromPercentage(timeModifierPercentage)
  );

  const [extraPercentage, setExtraPercentage] = useState(
    getTimePercentage() || 0
  );

  const [customValue, setCustomValue] = useState(extraPercentage);

  const updateTimeAllowanceOptions = {
    onError: (error: { message?: string }) => {
      handleMsgType({ type: TNotification.error, message: error?.message });
    },
    refetchQueries: [
      {
        query: StageCandidateFindManyDocument,
        variables: {
          candidateId: candidateId,
        },
      },
    ],
    onCompleted: () => {
      closeModal(ModalTypes.MITIGATE_CIRCUMSTANCE_MODAL);
    },
  };

  const [updateCognitiveTimeAllowance] =
    useUpdateCognitiveTestTimeAllowanceMutation(updateTimeAllowanceOptions);
  const [updateCoderbyteTimeAllowance] =
    useUpdateCoderbyteAssessmentTimeAllowanceMutation(
      updateTimeAllowanceOptions
    );

  const handleCustomValueChange = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;

    // Remove leading zeros
    const formatValue = parseFloat(value);
    const newValue = value === '' ? 0 : formatValue;

    if (!isNaN(formatValue) || value === '') {
      setCustomValue(newValue);
      setExtraPercentage(newValue);
      setDisplayTimeValue(calculateTimeModifierFromPercentage(newValue / 100));
    }
  };

  const handleCloseModal = () => {
    closeModal(ModalTypes.MITIGATE_CIRCUMSTANCE_MODAL);
  };

  const handleSubmit = () => {
    const updateTimeAllowance = isCoderbyte
      ? updateCoderbyteTimeAllowance
      : updateCognitiveTimeAllowance;
    updateTimeAllowance({
      variables: {
        id: testActionCandidate.id,
        timeModifierPercentage: extraPercentage / 100,
      },
    });
  };

  return (
    <Modal className={classes.modalPaper} onClose={handleCloseModal}>
      <Grid container direction="column" spacing={1}>
        <Grid>
          <Typography
            sx={{ fontWeight: '500', fontSize: '1.2rem' }}
          >{`${getTestActionName(testActionCandidate)} test`}</Typography>
        </Grid>
        <Grid sx={{ display: 'flex', alignItems: 'center', marginTop: '24px' }}>
          <p>
            Extend {isCoderbyte ? 'assessment' : 'testlet'} time by{' '}
            <input
              type="text"
              style={{
                border: '1px solid grey',
                outline: 'none',
                width: '50px',
                padding: '10px',
                marginRight: '10px',
                borderRadius: '5px',
                marginLeft: '8px',
              }}
              value={customValue}
              onChange={handleCustomValueChange}
            />
          </p>
          <p>percent</p>
        </Grid>
        <Grid>
          {defaultTestTime === null ? (
            <Typography
              sx={{ fontSize: '14px', paddingTop: '8px', color: '#62748c' }}
            >
              This assessment's time limit is unknown, so Coderbyte will not
              be sent any extra time.
            </Typography>
          ) : (
            <>
              <Typography
                sx={{ fontSize: '14px', paddingTop: '8px', color: '#62748c' }}
              >
                Default time: {defaultTestTime} {timeUnit}
              </Typography>
              <Typography
                sx={{ fontSize: '14px', paddingTop: '8px', color: '#62748c' }}
              >
                Adjusted time: {displayTimeValue} {timeUnit}
              </Typography>
            </>
          )}
        </Grid>

        <Grid
          item
          container
          direction="row"
          justifyContent="space-between"
          className={classes.btnContainer}
          style={{ marginTop: '20px', gap: '16px', width: '100%' }}
        >
          <Button
            variant="secondary"
            className={classes.btn}
            onClick={handleCloseModal}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            className={classes.btn}
            onClick={handleSubmit}
          >
            Update
          </Button>
        </Grid>
      </Grid>
    </Modal>
  );
};

export default MitigateCircumstanceModal;
